import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { objectOmit } from '@/_shared/helpers';
import { GenericGet } from '@/_shared/types';
import { GenericApiError } from '@/_shared/types/apiError';
import { getCurrentOrganization } from '@/core/state/selectors';
import { API_ORGANIZATIONS_ORGANIZATIONID_DEPARTMENTS_DEPARTMENTID_MEMBERS_PATH_KEY } from '@/swagger/constants/anagraphics-keys';
import { API_GET_DEPARTMENT_MEMBERS, KEY_GET_DEPARTMENT_MEMBERS } from '../constants';
import { MOCK_RESPONSE_GET_DEPARTMENT_MEMBERS } from '../mocks';

type GenericGetDepartmentMembersType = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_DEPARTMENTS_DEPARTMENTID_MEMBERS_PATH_KEY
>;

type ResponseGetDepartmentMembers = GenericGetDepartmentMembersType['response']['body'];

type RequestGetDepartmentMembers = GenericGetDepartmentMembersType['request']['path'] &
  GenericGetDepartmentMembersType['request']['query'];

type CallParamsGetDepartmentMembers = Omit<RequestGetDepartmentMembers, 'organizationId'> & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

export type CallResponseGetDepartmentMembers = ResponseGetDepartmentMembers;

type CallErrorGetDepartmentMembers = GenericApiError;

type CallSelectorsGetDepartmentMembers = {
  allEmployees: { id: string; name: string; email: string; role: string }[];
};

export const getDepartmentMembersCall = createGeneralCall<
  CallParamsGetDepartmentMembers,
  CallResponseGetDepartmentMembers,
  CallErrorGetDepartmentMembers,
  CallSelectorsGetDepartmentMembers
>({
  id: KEY_GET_DEPARTMENT_MEMBERS,
  method: 'get',
  url:
    ({ payload: { departmentId, ...others } }) =>
    state => {
      const org = getCurrentOrganization(state);
      const othersParams = objectOmit(others, ['errorSaga', 'successSaga']);
      return API_GET_DEPARTMENT_MEMBERS(org, departmentId, othersParams);
    },
  paramsSelector:
    ({ payload }) =>
    () => {
      return payload;
    },
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_GET_DEPARTMENT_MEMBERS : undefined,
  selectors: {
    allEmployees: ({ selectors }) =>
      createSelector(selectors.response, employees => {
        if (!employees?.length) {
          return [];
        }
        return (employees || []).reduce(
          (acc, employer) => {
            if (employer) {
              acc.push({
                id: employer.id || '',
                name: `${employer.firstName} ${employer.lastName}`,
                role: employer.role || '-',
                email: employer.email || '-',
              });
            }
            return acc;
          },
          [] as { id: string; name: string; email: string; role: string }[],
        );
      }),
  },
});
